import React, {useEffect, useState, memo} from 'react'
import { createPortal } from 'react-dom'
import { formatChartNumber } from './Utilities'
import Chart from 'react-apexcharts';
import ViewerModal from './ViewerModal';
import axios from 'axios';
import { formatEffectiveUnitLabel, getEffectiveUnit, buildHighlightLabelPoints, minMax } from '../utils/chartLabels';
import { buildComparisonSeriesData } from '../utils/seriesChartOptions';

function SeriesWidget({editFlag, data, gridHeight, wid, openCallback, deleteCallback, updateCallback}) {

  const [categories, setCategories] = useState([])
  const [ydata, setYdata] = useState([])
  const [xAnnotation, setXannotation] = useState([])
  const [yAnnotation, setYannotation] = useState([])
  const [highlightPoints, setHighlightPoints] = useState([])

  const [colors, setColors] = useState([])
  const [title, setTitle] = useState('')
  const [subtitle, setSubtitle] = useState('')
  const [yaxisConfig, setYaxisConfig] = useState({ title: { text: '' } })
  const [viewerModal, setViewerModal] = useState(false)
  const [loadingRefresh, setLoadingRefresh] = useState(false)
  const widgetChart = data?.meta_data?.chart || {}
  const widgetFrequency = widgetChart?.frequency?.value || data?.frequency || 'Annual'
  const widgetEffectiveUnit = getEffectiveUnit(
    data?.data,
    data?.effective_unit || widgetChart?.effectiveUnit || data?.unit
  )

  //   let chartOptions = {}
  const chartOptions = {
        chart: {
            id: 'basic-line-chart',
            toolbar: {
                show: false,
            },
            zoom: {
                enabled: false,
            },
        },
        title: {
            text: title,
            align: 'center',
            margin: 0,
            // offsetX: 0,
            // offsetY: 0,
            // floating: true,
            style: {
                fontSize:  '17px',
                fontWeight:  'bold',
                fontFamily:  'Open Sans',
                color:  '#333333'
            },
        },
        subtitle: {
            text: subtitle,
            align: 'center',
            margin: 0,
            // offsetX: 0,
            // offsetY: 0,
            // floating: true,
            style: {
              fontSize:  '15px',
              fontWeight:  'regular',
              fontFamily:  'Open Sans',
              color:  '#949494'
            },
        },
        stroke: {
            width: 3,
            curve: 'smooth'
        },
        colors: colors,
        plotOptions: {
            bar: {
                columnWidth: '100%',
                barHeight: '100%',
                borderRadius: 1,


            },
            area: {
                fillTo: 'origin'
            },
            line: {
                isSloperChart: true,
            }
        },
        xaxis: {
            title: {
                text: 'Date',
                style: {
                    fontSize: '11px',
                    fontFamily: 'sans-serif',
                    color: '#8b8b8b',
                }
            },
            labels: {
                rotate: 0,
                rotateAlways: true,
                style: {
                    fontSize: '11px',
                    fontFamily: 'sans-serif'
                }
            },
            axisTicks: {
                show: false,
            },
            tickAmount: 6,
            // type: 'datetime',
            categories: categories,
        },
        yaxis: yaxisConfig,
        fill: {
            opacity: 0.2,
            type: ydata.map(item => item.type === 'area' ? 'solid': 'gradient'),
            gradient: {
                opacityFrom: 1,
                shadeIntensity: 0,
            }
        },
        grid:
        {
            show: false,
        },
        legend: {
            show: widgetChart?.legend !== false,
            position: 'top',
            // horizontalAlign: 'left',
            fontSize: '14px',
            fontFamily: 'Open Sans',
            fontWeigth: '300',
            floating: true,
            offsetY: -20,
            labels: {
                colors: ['#333333']
            }
        },
        annotations: {
            yaxis: yAnnotation,
            xaxis: xAnnotation,
            points: highlightPoints
        }
    }



    const series = (ydata.length > 0) ? ydata : [{name: '', data: []}]
    const chartHeight = Math.max(220, ((Number(gridHeight) || 4) * 100) + (((Number(gridHeight) || 4) - 1) * 20) - 90)

    useEffect(() => {


        const countryList = Array.isArray(data?.country_list) ? data.country_list : []
        const rows = Array.isArray(data?.data) ? data.data : []
        const xaxis = Array.isArray(data?.xaxis) ? data.xaxis : []
        const yaxis = Array.isArray(data?.yaxis) ? data.yaxis : []
        const highlight = Array.isArray(data?.highlight) ? data.highlight : []
        const chartSettings = data?.meta_data?.chart || {}
        const frequency = chartSettings?.frequency?.value || data?.frequency || 'Annual'

        if(Object.keys(data || {}).length > 0 && countryList.length > 0){
            setTitle(data.series_name)
            const customSubtitle = chartSettings?.customSubtitle?.trim()
            setSubtitle(customSubtitle || (data.indicator + " " + (frequency ? `(${frequency})` : '')))

            const series2 = data?.series2
            const series2Rows = Array.isArray(data?.series2Data) ? data.series2Data : []
            const hasSeries2 = Boolean(series2 && Array.isArray(series2.country_list) && series2.country_list.length > 0 && series2Rows.length > 0)

            const primaryDescriptor = {
                countryList,
                rows,
                frequency,
                unitLabel: widgetChart?.effectiveUnitLabel?.trim() || formatEffectiveUnitLabel(widgetEffectiveUnit),
            }
            const secondaryDescriptor = hasSeries2 ? {
                countryList: series2.country_list,
                rows: series2Rows,
                frequency: series2.frequency || 'Annual',
                unitLabel: getEffectiveUnit(series2Rows, series2.unit) ? formatEffectiveUnitLabel(getEffectiveUnit(series2Rows, series2.unit)) : (series2.unit || ''),
            } : null

            const yDomain = {
                min: chartSettings?.yMin !== '' && chartSettings?.yMin !== undefined ? Number(chartSettings.yMin) : undefined,
                max: chartSettings?.yMax !== '' && chartSettings?.yMax !== undefined ? Number(chartSettings.yMax) : undefined,
            }

            const built = buildComparisonSeriesData(primaryDescriptor, secondaryDescriptor, Boolean(chartSettings?.useSecondaryAxis), yDomain)

            let rawCategories = built.rawCategories
            let displayCategories = built.categories
            let series = built.series

            const filterYearRange = chartSettings?.yearRange || 'max'
            const filterFromYear = chartSettings?.fromYear
            const filterToYear = chartSettings?.toYear
            const filterXMin = chartSettings?.xMin
            const filterXMax = chartSettings?.xMax

            if (filterYearRange !== 'max' || filterFromYear || filterToYear || filterXMin || filterXMax) {
                const keepIndexes = []
                rawCategories.forEach((item, index) => {
                    const itemYear = parseInt(String(item).split('-')[0], 10)
                    const from = filterFromYear ? Number(filterFromYear) : null
                    const to = filterToYear ? Number(filterToYear) : null
                    const minXYear = filterXMin ? parseInt(String(filterXMin).split('-')[0], 10) : null
                    const maxXYear = filterXMax ? parseInt(String(filterXMax).split('-')[0], 10) : null

                    if (from && (!itemYear || itemYear < from)) return
                    if (to && (!itemYear || itemYear > to)) return
                    if (minXYear && (!itemYear || itemYear < minXYear)) return
                    if (maxXYear && (!itemYear || itemYear > maxXYear)) return
                    keepIndexes.push(index)
                })
                rawCategories = keepIndexes.map(index => rawCategories[index])
                displayCategories = keepIndexes.map(index => displayCategories[index])
                series = series.map(item => ({ ...item, data: keepIndexes.map(index => item.data[index]) }))
            }

            const x = rawCategories
            setCategories(displayCategories)

            const withLabels = yaxisEntry => ({
                ...yaxisEntry,
                labels: {
                    style: { fontSize: '11px', fontFamily: 'sans-serif' },
                    formatter: value => formatChartNumber(value, 2),
                },
            })
            setYaxisConfig(Array.isArray(built.yaxis) ? built.yaxis.map(withLabels) : withLabels(built.yaxis))

            setColors(series.map(item => item.color))

            let y = series
            setYdata(y)


            // Must match how `categories` above formats xdata exactly, or
            // ApexCharts can't find the category to anchor the annotation
            // to and silently draws nothing. For Annual series, categories
            // are just the year ("2000") — annotations that always appended
            // " - MM" ("2000 - 12") never matched anything, which is why
            // x-axis annotations and highlight sections silently failed to
            // render for Annual series specifically (Monthly/Quarterly
            // happened to work since both sides used the same "YYYY - MM").
            const formatCategoryValue = dateStr => (
                widgetFrequency === 'Annual' ? dateStr.split('-')[0] : dateStr.split('-')[0] + ' - ' + dateStr.split('-')[1]
            )

            let xanntemp = xaxis.filter(item => item.value !== '').map(item => {
                return {
                    x: formatCategoryValue(item.value),
                    // x2: '2022 - 01',

                    strokeDashArray: 4,
                    borderColor: '#B6B6B6',
                    fillColor: '#B6B6B6',
                    opacity: 1
                }
            })

            let highlighttemp = highlight.filter(item => item.x1?.value !== '' && item.x2?.value !== '').map(item => {
                const color = item.color || '#B6B6B6'
                const opacity = item.opacity ?? 0.5
                return {
                    x: formatCategoryValue(item.x1.value),
                    x2: formatCategoryValue(item.x2.value),
                    // x2: '2022 - 01',

                    strokeDashArray: 4,
                    borderColor: color,
                    fillColor: color,
                    opacity,
                    // No `label` here — see buildHighlightLabelPoints below for why.
                }
            })

            // let d =  [...xanntemp]
            let d =  [...xanntemp, ...highlighttemp]
            setXannotation(d)

            try {
                const allVisibleValues = y.flatMap(item => item.data).filter(value => value !== null && value !== undefined && !Number.isNaN(Number(value)))
                const [rawMin, rawMax] = minMax(allVisibleValues)
                const domainMin = chartSettings?.yMin !== '' && chartSettings?.yMin !== undefined ? Number(chartSettings.yMin) : (allVisibleValues.length ? rawMin : 0)
                const domainMax = chartSettings?.yMax !== '' && chartSettings?.yMax !== undefined ? Number(chartSettings.yMax) : (allVisibleValues.length ? rawMax : 1)
                setHighlightPoints(buildHighlightLabelPoints(highlight, x, formatCategoryValue, domainMin, domainMax))
            } catch (error) {
                // A broken highlight label must never take the widget down
                // with it — worst case, the text label is just missing.
                console.error('Failed to compute highlight label positions:', error)
                setHighlightPoints([])
            }

            let yanntemp = yaxis.filter(item => item.value !== '').map(item => {
                return {
                    y: item.value,
                    // x2: '2022 - 01',

                    strokeDashArray: 4,
                    borderColor: '#B6B6B6',
                    fillColor: '#B6B6B6',
                    opacity: 1,
                    label: {
                        text: item.text,
                        position: 'left',
                        offsetX: 25,
                        style: {
                            background: '#2aac9c',
                            color: '#ffffff',
                            fontFamily: 'Open Sans',
                        }
                    }
                }
            })

        //     let metrictemp = metricValue.filter(item => item.value != '').map(item => {

        //         let met = 0
        //         countryValue.filter(e => e.value != '').forEach(country => {
        //             if(country.countryType == 'country'){
        //                 let gg = data.data_dict.Countries[country.value]?.statistics?.[`${frequencyValue.value}_${unitValue.value}`]?.[item.value]
        //                 met += parseFloat(gg)
        //             }
        //             else if(country.countryType == 'region'){
        //                 let gg = data.data_dict.Regions[country.value]?.statistics?.[`${frequencyValue.value}_${unitValue.value}`]?.[item.value]
        //                 met += parseFloat(gg)
        //             }
        //         })
        //         // console.log('met', met)

        //         let metricY = null
        //         if(met){
        //             metricY = met / countryValue.filter(e => e.value != '').length
        //         }

        //         if(metricY){

        //             return {
        //                 y: metricY,
        //                 strokeDashArray: 1,
        //                 borderColor: '#333333',
        //                 fillColor: '#333333',
        //                 opacity: 1,
        //                 label: {
        //                     text: item.label,
        //                     style: {
        //                         background: '#577bab',
        //                         color: '#ffffff',
        //                         fontFamily: 'Open Sans',
        //                         // padding: {
        //                         //     top: 2,
        //                         //     bottom: 5,
        //                         // }
        //                     }
        //                 }
        //             }
        //         }
        //     })
            let yfinal =  [...yanntemp]
        //     let yfinal =  [...yanntemp, ...metrictemp]

            setYannotation(yfinal)
        }
    }, [data])

    const refreshDataHandler = async () => {
        if (!updateCallback || !data?.meta_data || loadingRefresh) return

        const countryRows = (data?.country_list || []).filter(item => item.countryType !== 'region')
        if (countryRows.length === 0) return

        setLoadingRefresh(true)
        try {
            const response = await axios.post('/api/v1/data/request/', {
                topic: data.meta_data.topic ? [data.meta_data.topic] : [],
                indicator: [data.indicator || data.meta_data.indicator],
                country: countryRows.map(item => item.country),
                frequency: data.frequency || data.meta_data.chart?.frequency?.value || 'Annual',
            }, { timeout: 30000 })

            const item = response.data?.data_list?.[0]
            const unit = data.unit || data.meta_data.chart?.unit?.value || ''
            const transformation = data.transformation || data.meta_data.chart?.transformation?.value || ''
            const countryNames = new Set(countryRows.map(row => row.country))
            const refreshedRows = (item?.data || []).filter(row => (
                row.unit === unit &&
                String(row.transformation || '').trim() === String(transformation || '').trim() &&
                countryNames.has(row.country)
            ))

            if (refreshedRows.length === 0) return

            const regionRows = (data.data || []).filter(row => !countryNames.has(row.country))
            updateCallback(wid, {
                ...data,
                data: [...regionRows, ...refreshedRows],
                effective_unit: getEffectiveUnit(refreshedRows, data.effective_unit || data.unit),
            })
        } finally {
            setLoadingRefresh(false)
        }
    }

    const stopGridDragEvent = (event) => {
        event.preventDefault()
        event.stopPropagation()
        if (event.nativeEvent?.stopImmediatePropagation) {
            event.nativeEvent.stopImmediatePropagation()
        }
    }

    const openViewerHandler = (event) => {
        stopGridDragEvent(event)
        setViewerModal(true)
    }

    const refreshClickHandler = (event) => {
        stopGridDragEvent(event)
        refreshDataHandler()
    }

    const editClickHandler = (event) => {
        stopGridDragEvent(event)
        openCallback(wid)
    }

    const deleteClickHandler = (event) => {
        stopGridDragEvent(event)
        deleteCallback(wid)
    }


  return (
    <div style={{backgroundColor: data?.backgroundColor || '#ffffff'}} className='dwidget-container'>
        <div style={{position: 'relative'}} className='h-100'>
            {editFlag && (
                <div style={{position: 'absolute', right: '0', zIndex:'1000'}}>
                    <button
                        type='button'
                        onMouseDown={refreshClickHandler}
                        className='dwidget-icon-action'
                        title='Refresh data'
                        style={{ cursor: loadingRefresh ? 'wait' : 'pointer' }}
                    >
                        <i className={`fa-solid fa-arrows-rotate ${loadingRefresh ? 'fa-spin' : ''}`}></i>
                    </button>
                    {Object.keys(data || {}).length > 0 && (
                        <button
                            type='button'
                            onMouseDown={openViewerHandler}
                            className='dwidget-icon-action dwidget-viewer-action'
                            title='Open viewer'
                        >
                            <i className="fa-solid fa-up-right-and-down-left-from-center"></i>
                        </button>
                    )}
                    <i onMouseDown={editClickHandler} style={{color: '#808080', cursor: 'pointer'}} className="fa-regular fa-pen-to-square ps-1"></i>
                    <i onMouseDown={deleteClickHandler} style={{color: '#808080', cursor: 'pointer'}} className="fa-regular fa-circle-xmark px-2 pt-2"></i>
                </div>
            )}
            <div style={{boxSizing: 'border-box', paddingTop: '1em'}} className='d-flex justify-content-center align-items-center h-100'>
                <div className='px-4 h-100 w-100 d-flex justify-content-center align-items-center' style={{flex: '1'}}>
                    {Object.keys(data || {}).length == 0 ? (
                        <div onMouseDown={editFlag ? () => openCallback?.(wid) : undefined} style={{color: '#8b8b8b', cursor: editFlag ? 'pointer' : 'default'}} className='text-center'>
                            {editFlag ? <>Click edit button <br />to edit the series</> : 'No content available.'}

                            {/* {editFlag && (
                                <i onMouseDown={e => openCallback(wid)} style={{color: '#808080', cursor: 'pointer'}} className="fa-regular fa-pen-to-square"></i>
                            )} */}
                        </div>
                    ): (
                        <Chart options={chartOptions} series={series} className={'pt-2'} style={{flex: '1'}} type="line" width={'100%'} height={chartHeight}  />
                    )}
                </div>
            </div>
        </div>
        {viewerModal && data?.meta_data && createPortal(
            <ViewerModal
                meta={data.meta_data}
                closeCallback={() => setViewerModal(false)}
                onApplyToDashboard={content => updateCallback(wid, content)}
            />,
            document.body
        )}
        {/* <div className='d-flex justify-content-between'>
            <div className='flex-grow-1 pt-4'>
                <p className='dwidget-chart-title'>{data.series_name}</p>
                <p className='dwidget-chart-subtitle'>{data.frequency}</p>
            </div>
            <div>
                <i style={{color: '#808080', cursor: 'pointer'}} className="fa-regular fa-pen-to-square"></i>
                <i style={{color: '#808080', cursor: 'pointer'}} className="fa-regular fa-circle-xmark px-2 pt-2"></i>
            </div>
        </div>
        <div className='px-4' style={{flex: '1'}}>
            <Chart options={chartOptions} series={series} type="line" width={'100%'} height={'100%'}  />
        </div> */}

    </div>
  )
}

export default memo(SeriesWidget)
